import React, { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Activity,
  Loader2,
  User,
  Search,
  ChevronDown,
  Users,
  Check,
  CalendarRange,
  AlertCircle,
  FileText,
  Monitor,
  Briefcase,
  Sparkles,
} from "lucide-react";
import { format } from "date-fns";
import { useToast } from "../hooks/useToast";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "../components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";

import api from "../api/api";
import { getEmployees } from "../api/employees";
import PersonAvatar from "../components/people/PersonAvatar";
import {
  compareDays,
  pickComparisonDays,
  type DayComparison,
  type MetricDelta,
  type TrackingDay,
} from "../utils/dayComparison";
import {
  changeKind,
  prettyArea,
  splitWorkAreas,
  workAreaRows,
  type AreaRow,
  type RankedArea,
  type TimelineEntry,
} from "../utils/workAreas";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface EmployeeRecord {
  employeeID: string;
  employeeName: string;
  work_Start: string | null;
  work_end: string | null;
  date: string;
  WorkFromHome: boolean;
  in_time: {
    attendance: string | null;
    work_monitor: string | null;
    work_activity: string | null;
    difference_minutes: number;
  };
  out_time: {
    attendance: string | null;
    work_monitor: string | null;
    work_activity: string | null;
    difference_minutes: number;
  };
  /** Time on the clock per source: attendance's punch span against the stretch
   *  WorkMonitor saw activity in. */
  total_time?: {
    attendance_hours: number;
    work_monitor_hours?: number;
    work_activity_hours: number;
    difference_minutes: number;
  };
  /** Only WorkMonitor measures idle. attendance_hours and difference_minutes
   *  come back null — a punch pair says somebody was present, not what they
   *  did inside it, so there is nothing to compare against. */
  idle_time: {
    attendance_hours: number | null;
    work_monitor_hours?: number | null;
    work_activity_hours: number;
    difference_minutes: number | null;
  };
  status: {
    present: boolean;
    absent: boolean;
    leave: boolean;
    work_from_home: boolean;
  };
  leave: {
    present: boolean;
    absent: boolean;
    leave: boolean;
    work_from_home: boolean;
  };
}

interface Employee {
  employeeID: string;
  employee_name?: string;
  user_id?: string;
  first_name?: string;
  last_name?: string;
  id?: string;
}

type StatusFilterType = "All" | "Present" | "Absent" | "Leave" | "WFH";

/**
 * The three date inputs on this page, sized the same.
 *
 * The Input default is h-10 with px-3 py-2, meant for a text field. Shrunk to
 * h-8, that padding leaves 16px of content box for a native date control —
 * which has to fit "13-Oct-2026" AND the browser's own calendar picker button.
 * The picker ended up clipped against the right edge on both boxes.
 *
 * So: a little more width, less horizontal padding, and no vertical padding at
 * all — h-8 already sets the height and the text centres itself in it.
 *
 * md:text-xs is not a duplicate of text-xs. The Input default is
 * "text-base md:text-sm"; tailwind-merge drops text-base for our text-xs but
 * md:text-sm is a different variant and survives, so on any desktop screen
 * these boxes were rendering at 14px while the Load button beside them sat at
 * 12px — part of why they looked cramped and out of place.
 */
const DATE_INPUT_CLASS =
  "w-[150px] h-8 px-2 py-0 text-xs md:text-xs bg-white [&::-webkit-calendar-picker-indicator]:ml-0 [&::-webkit-calendar-picker-indicator]:cursor-pointer";

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmtTime = (t: string | null): string => {
  if (!t) return "—";
  const raw = t.includes(" ") ? t.split(" ")[1] : t;
  const parts = raw.split(":");
  return `${parts[0]}:${parts[1]}`;
};

const fmtHours = (h: number): string => {
  if (h === 0) return "—";
  const hrs = Math.floor(h);
  const mins = Math.round((h - hrs) * 60);
  return mins > 0 ? `${hrs}h ${mins}m` : `${hrs}h`;
};

const fmtDate = (d: string): string => {
  try {
    return format(new Date(d), "dd MMM yyyy");
  } catch {
    return d;
  }
};

/** Minutes as something to read: "6m", "1h 12m", "4h". fmtHours renders a
 *  six-minute day as "0h 6m", which is the figure this card leans on most. */
const fmtMins = (mins: number): string => {
  const m = Math.round(mins || 0);
  if (m <= 0) return "—";
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h}h ${rest}m` : `${h}h`;
};

const getOverallStatus = (s: EmployeeRecord["status"]): StatusFilterType => {
  if (s.absent) return "Absent";
  if (s.leave) return "Leave";
  if (s.work_from_home) return "WFH";
  if (s.present) return "Present";
  return "All";
};

// Normalize raw API response (array or wrapped object) to EmployeeRecord[]
const normalizeReport = (raw: unknown): EmployeeRecord[] => {
  if (Array.isArray(raw)) return raw as EmployeeRecord[];
  if (raw && typeof raw === "object") {
    const obj = raw as Record<string, unknown>;
    if (Array.isArray(obj.report)) return obj.report as EmployeeRecord[];
  }
  return [];
};

// ─── Table Cell Components ──────────────────────────────────────────────────────

// `value` is optional because `na` short-circuits before it is read, and every
// caller that passes `na` passes nothing else — which the required type had
// been quietly rejecting at seven call sites.
const StatusCell: React.FC<{ value?: boolean | null; na?: boolean }> = ({
  value,
  na,
}) => {
  if (na) return <span className="text-slate-400 text-[10px]">NA</span>;
  return value ? (
    <span className="text-emerald-600 font-semibold text-[10px]">Yes</span>
  ) : (
    <span className="text-slate-400 text-[10px]">No</span>
  );
};

const DiffCell: React.FC<{ mins: number; na?: boolean }> = ({ mins, na }) => {
  if (na) return <span className="text-slate-400 text-[10px]">NA</span>;
  const color =
    mins === 0
      ? "text-slate-500"
      : mins <= 5
        ? "text-amber-600"
        : "text-red-600";
  return (
    <span className={`text-[10px] font-medium ${color}`}>
      {mins === 0 ? "0 min" : `${mins} min`}
    </span>
  );
};

// ─── EmployeeTableCard ──────────────────────────────────────────────────────────

const EmployeeTableCard: React.FC<{ emp: EmployeeRecord }> = ({ emp }) => {
  const status = getOverallStatus(emp.status);
  const statusColor =
    status === "Present"
      ? "bg-emerald-50 text-emerald-700 border-emerald-200"
      : status === "Absent"
        ? "bg-red-50 text-red-700 border-red-200"
        : status === "Leave"
          ? "bg-blue-50 text-blue-700 border-blue-200"
          : status === "WFH"
            ? "bg-purple-50 text-purple-700 border-purple-200"
            : "bg-slate-50 text-slate-600 border-slate-200";

  const rows = [
    {
      label: "In Time",
      attendance: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.in_time.attendance)}
        </span>
      ),
      monitor: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.in_time.work_monitor)}
        </span>
      ),
      activity: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.in_time.work_activity)}
        </span>
      ),
      diff: <DiffCell mins={emp.in_time.difference_minutes} />,
    },
    {
      label: "Out Time",
      attendance: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.out_time.attendance)}
        </span>
      ),
      monitor: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.out_time.work_monitor)}
        </span>
      ),
      activity: (
        <span className="text-[10px] text-slate-700">
          {fmtTime(emp.out_time.work_activity)}
        </span>
      ),
      diff: <DiffCell mins={emp.out_time.difference_minutes} />,
    },
    {
      // Time on the clock, both sides. This is the comparison the old "Idle
      // Time" row was actually making — it just said idle on the label.
      label: "Total Time",
      attendance: (
        <span className="text-[10px] text-slate-700">
          {fmtHours(emp.total_time?.attendance_hours ?? 0)}
        </span>
      ),
      monitor: (
        <span className="text-[10px] text-slate-700">
          {fmtHours(emp.total_time?.work_monitor_hours ?? 0)}
        </span>
      ),
      activity: (
        <span className="text-[10px] text-slate-700">
          {fmtHours(emp.total_time?.work_activity_hours ?? 0)}
        </span>
      ),
      diff: <DiffCell mins={emp.total_time?.difference_minutes ?? 0} />,
    },
    {
      // Real idle, and only where it is measured. NA beats a confident 0 in
      // the two columns that have nothing to say.
      label: "Idle Time",
      attendance: <StatusCell na />,
      monitor: <StatusCell na />,
      activity: (
        <span className="text-[10px] text-slate-700">
          {fmtHours(emp.idle_time.work_activity_hours)}
        </span>
      ),
      diff: <StatusCell na />,
    },
    {
      label: "Present",
      attendance: <StatusCell value={emp.status.present} />,
      monitor: <StatusCell value={emp.status.present} />,
      activity: <StatusCell value={emp.status.present} />,
      diff: <StatusCell na />,
    },
    {
      label: "Absent",
      attendance: <StatusCell value={emp.status.absent} />,
      monitor: <StatusCell value={emp.status.absent} />,
      activity: <StatusCell value={emp.status.absent} />,
      diff: <StatusCell na />,
    },
    {
      label: "Leave",
      attendance: <StatusCell value={emp.leave.leave} />,
      monitor: <StatusCell value={emp.leave.leave} />,
      activity: <StatusCell value={emp.leave.leave} />,
      diff: <StatusCell na />,
    },
    {
      label: "Work From Home",
      attendance: <StatusCell value={emp.status.work_from_home} />,
      monitor: <StatusCell value={emp.status.work_from_home} />,
      activity: <StatusCell value={emp.status.work_from_home} />,
      diff: <StatusCell na />,
    },
  ];

  return (
    <Card className="border border-slate-200 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b border-slate-200">
        <div className="flex items-center gap-2">
          {/* The row knows this person only by their employee CODE, so the
              photo comes through the directory's code bridge. */}
          <PersonAvatar employeeCode={emp.employeeID} name={emp.employeeName} size={24} />
          <div>
            <p className="text-xs font-semibold text-slate-800">
              {emp.employeeName}
            </p>
            <p className="text-[9px] text-slate-500">
              {emp.employeeID}&nbsp;•&nbsp;{fmtDate(emp.date)}
            </p>
          </div>
        </div>
        <Badge className={`text-[9px] px-2 h-5 border ${statusColor}`}>
          {status}
        </Badge>
      </div>

      <div className="overflow-auto max-h-[60vh]">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="bg-slate-700 text-white">
              <th className="sticky top-0 z-10 bg-slate-700 px-3 py-1.5 text-[10px] font-semibold w-28">
                Particulars
              </th>
              <th className="sticky top-0 z-10 bg-slate-700 px-3 py-1.5 text-[10px] font-semibold text-center">
                Attendance
              </th>

              <th className="sticky top-0 z-10 bg-slate-700 px-3 py-1.5 text-[10px] font-semibold text-center">
                Work Activity
              </th>
              <th className="sticky top-0 z-10 bg-slate-700 px-3 py-1.5 text-[10px] font-semibold text-center">
                Diff (Activity vs Attendance)
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr
                key={row.label}
                className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}
              >
                <td className="px-3 py-1.5 text-[10px] font-medium text-slate-600 border-r border-slate-100">
                  {row.label}
                </td>
                <td className="px-3 py-1.5 text-center border-r border-slate-100">
                  {row.attendance}
                </td>

                <td className="px-3 py-1.5 text-center border-r border-slate-100">
                  {row.activity}
                </td>
                <td className="px-3 py-1.5 text-center">{row.diff}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

/* ─── Day vs day ────────────────────────────────────────────────────────────
 * What WorkMonitor recorded on one day against another. The Daily Breakdown
 * below already lists each day on its own card, which answers "what happened"
 * a day at a time — this answers "what changed", which otherwise means reading
 * two cards and subtracting in your head.
 *
 * The two days are the ends of the range the date pickers already set, so
 * there is nothing extra to choose.
 */

/** "-45m" / "+2h 15m" / "0m". Signed on purpose — the sign IS the finding. */
const fmtDeltaMins = (m: number): string => {
  if (!m) return "0m";
  // fmtMins, not fmtHours: fmtHours spells three minutes "0h 3m", so a small
  // drop rendered as "−0h 3m" — a leading zero the reader has to parse past
  // on every short row.
  return `${m > 0 ? "+" : "−"}${fmtMins(Math.abs(m))}`;
};

const fmtMetricValue = (value: number, unit: MetricDelta["unit"]): string => {
  if (unit === "minutes") return fmtHours(value / 60);
  if (unit === "percent") return `${value}%`;
  return String(value);
};

const fmtMetricDelta = (m: MetricDelta): string => {
  if (!m.delta) return "no change";
  if (m.unit === "minutes") return fmtDeltaMins(m.delta);
  const sign = m.delta > 0 ? "+" : "−";
  const size = Math.abs(m.delta);
  return `${sign}${size}${m.unit === "percent" ? "%" : ""}`;
};

const DeltaRow: React.FC<{ m: MetricDelta }> = ({ m }) => {
  // A count going either way is just a fact — a day split into three stretches
  // is not worse than one split into two, so counts stay neutral rather than
  // colouring a lunch break red.
  const neutral = m.unit === "count" || m.delta === 0;
  const good = m.higherIsBetter ? m.delta > 0 : m.delta < 0;
  const tone = neutral
    ? "text-slate-500"
    : good
      ? "text-emerald-600"
      : "text-rose-600";

  return (
    <tr className="border-t border-slate-100">
      <td className="py-2 pl-3 pr-2 text-[11px] text-slate-600">{m.label}</td>
      <td className="py-2 px-2 text-[11px] text-right tabular-nums text-slate-700">
        {fmtMetricValue(m.before, m.unit)}
      </td>
      <td className="py-2 px-2 text-[11px] text-right tabular-nums font-semibold text-slate-900">
        {fmtMetricValue(m.after, m.unit)}
      </td>
      <td className={`py-2 pl-2 pr-3 text-[11px] text-right tabular-nums font-semibold ${tone}`}>
        {fmtMetricDelta(m)}
        {/* No percentage when the earlier day was zero: "up from nothing" has
            no percentage, and both ∞ and 100% would be lies. */}
        {m.percent !== null && m.delta !== 0 && m.unit !== "percent" && (
          <span className="ml-1 font-normal opacity-70">
            ({m.percent > 0 ? "+" : ""}{m.percent}%)
          </span>
        )}
      </td>
    </tr>
  );
};

/**
 * Both days as one table: a row per work area, a column per day.
 *
 * It replaces three lists sitting above two side-by-side tables. Those carried
 * the same areas twice — once as "on both days / only today / nothing today"
 * with no files attached, and again per day with the files in grey underneath
 * — so reading one area's whole story meant holding a name in your head and
 * hunting for it in the other place. Here the row IS the story.
 *
 * Colour follows DeltaRow above: a direction that carries no judgement stays
 * slate, because more time in an area is not better or worse than less. Only
 * "new" and "stopped" are coloured, and for findability rather than praise or
 * blame — "got no time today" is the row a manager is scanning for.
 */
/**
 * One day's half of a row: how long, and what was actually open in there.
 *
 * The files sit in the DAY's column rather than once under the area name,
 * because "it was report.py on the 13th and it is uploader.py today" is the
 * sentence a reader is trying to build. A single merged list under the name
 * cannot be read that way — it says these files were seen, some time, in one
 * of two days.
 */
const DayCell: React.FC<{
  mins: number;
  files: string[];
  /** The later day, which is the one being asked about. */
  strong?: boolean;
}> = ({ mins, files, strong }) => (
  <td className="py-2 px-2 align-top">
    <p
      className={`text-[11px] tabular-nums ${
        strong ? "font-semibold text-slate-900" : "text-slate-600"
      }`}
    >
      {fmtMins(mins)}
    </p>
    {files.length > 0 && (
      <p
        className="text-[9.5px] text-slate-400 leading-snug break-words mt-0.5"
        title={files.join(", ")}
      >
        {files.join(", ")}
      </p>
    )}
  </td>
);

/**
 * The change column for one row.
 *
 * Deliberately quiet. This said "stopped" in red, which reads as a verdict on
 * the person — work abandoned — when all the data supports is "this got no
 * time". The figures either side already say that, so the label's job is to
 * make the row findable, not to pass judgement on it. Only "new" keeps a
 * colour: it is the one genuinely new thing on the later day.
 */
const ChangeCell: React.FC<{ row: AreaRow; afterInProgress: boolean }> = ({
  row, afterInProgress,
}) => {
  const kind = changeKind(row, afterInProgress);
  return (
    <td className="py-2 pl-2 pr-3 text-[10.5px] text-right tabular-nums whitespace-nowrap">
      {kind === "new" ? (
        <span className="text-emerald-600 font-medium">new</span>
      ) : kind === "none-yet" ? (
        <span className="text-slate-400">none yet</span>
      ) : kind === "stopped" ? (
        <span className="text-slate-400">no time</span>
      ) : (
        <span className="text-slate-500">{fmtDeltaMins(row.delta)}</span>
      )}
    </td>
  );
};

const AreaTable: React.FC<{
  rows: AreaRow[];
  beforeDate: string;
  afterDate: string;
  /** False when one day is too thin for a change to mean anything. */
  showChange: boolean;
  /** The later day has not finished, so nothing can be called stopped yet. */
  afterInProgress: boolean;
}> = ({ rows, beforeDate, afterDate, showChange, afterInProgress }) => {
  const shown = rows.slice(0, 14);
  const rest = rows.slice(14);
  const restBefore = rest.reduce((sum, r) => sum + r.before, 0);
  const restAfter = rest.reduce((sum, r) => sum + r.after, 0);

  // The day columns are left-aligned because a list of filenames sits under
  // each figure; right-aligned numbers with ragged text beneath them read as
  // two columns that drifted apart.
  const head =
    "py-2 px-2 text-left text-[9px] uppercase tracking-wider text-slate-500 font-semibold";

  // No Card of its own: it is rendered inside the comparison card, under the
  // paragraph it belongs to. A card nested in a card reads as two findings.
  return (
    <div className="overflow-x-auto">
      {!shown.length ? (
        <p className="px-3 py-5 text-[11px] text-slate-500 text-center">
          Nothing tracked on either day.
        </p>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="bg-slate-50">
              <th className="py-2 pl-3 pr-2 text-left text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                Work area
              </th>
              <th className={`${head} w-[28%]`}>{fmtDate(beforeDate)}</th>
              <th className={`${head} w-[28%]`}>{fmtDate(afterDate)}</th>
              {showChange && (
                <th className={`${head} text-right pr-3 w-[14%]`}>Change</th>
              )}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.area} className="border-t border-slate-100 align-top">
                <td className="py-2 pl-3 pr-2">
                  <p className="text-[11px] text-slate-800" title={r.area}>
                    {prettyArea(r.area)}
                  </p>
                </td>
                <DayCell mins={r.before} files={r.beforeDetails} />
                <DayCell mins={r.after} files={r.afterDetails} strong />
                {showChange && (
                  <ChangeCell row={r} afterInProgress={afterInProgress} />
                )}
              </tr>
            ))}
            {rest.length > 0 && (
              <tr className="border-t border-slate-100">
                <td className="py-2 pl-3 pr-2 text-[10px] text-slate-400">
                  + {rest.length} smaller
                </td>
                <td className="py-2 px-2 text-[10px] tabular-nums text-slate-400">
                  {fmtMins(restBefore)}
                </td>
                <td className="py-2 px-2 text-[10px] tabular-nums text-slate-400">
                  {fmtMins(restAfter)}
                </td>
                {showChange && <td className="pr-3" />}
              </tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
};

/** What the model sent back, once it has been read. It writes the opening
 *  sentences and nothing else — which areas ran, and for how long, is counted
 *  from the tracked minutes in the table below (workAreaRows), so nothing can
 *  be named that was never tracked and no figure can disagree with the table. */
interface AiComparison {
  verdict: string;
  confidence: "high" | "low";
  confidence_note?: string;
}

const DayComparisonPanel: React.FC<{
  comparison: DayComparison;
  employeeId?: string;
  employeeName?: string;
}> = ({ comparison, employeeId, employeeName }) => {
  const { before, after, metrics, apps } = comparison;
  // Only what actually moved. An app that ran the same on both days is not a
  // finding, and a list padded with zeroes buries the ones that are.
  const moved = apps.filter((a) => a.delta !== 0).slice(0, 8);

  // Each day's work areas as its own ranked list. Recomputed only when the
  // pair changes: it walks a few hundred timeline entries per day.
  const areas = useMemo(
    () => splitWorkAreas(
      (before.timeline || []) as TimelineEntry[],
      (after.timeline || []) as TimelineEntry[],
    ),
    [before, after],
  );

  // The one table both days are read from: a row per area, a column per day.
  const rows = useMemo(
    () => workAreaRows(
      (before.timeline || []) as TimelineEntry[],
      (after.timeline || []) as TimelineEntry[],
    ),
    [before, after],
  );

  // Whether the two days can be set against each other at all. Decided HERE,
  // from the tracked minutes, rather than taken from the model's own
  // `confidence` — the figures are known for certain and the model's opinion
  // of them is not. Same 30-minute floor the prompt uses, so the banner and
  // the hidden lists always agree about which days are too thin.
  const comparable =
    Math.min(before.total_tracked_mins || 0, after.total_tracked_mins || 0) >= 30;

  // A day that has not finished is not a badly-tracked day. At 11am the later
  // day holds twenty minutes because it is twenty minutes old, and saying the
  // comparison is "unreliable" reads as a malfunction every single morning.
  // The lists still wait — they would be measuring the clock, not the work —
  // but the reason given has to be the true one.
  const afterIsToday = after.date === format(new Date(), "yyyy-MM-dd");

  const [ai, setAi] = useState<AiComparison | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Thrown away whenever the pair changes — a comparison of two other days
  // left on screen is worse than no comparison at all.
  useEffect(() => {
    setAi(null);
    setAiError(null);
  }, [before, after]);

  const askAi = async () => {
    setAiLoading(true);
    setAiError(null);
    const asDay = (d: TrackingDay, list: RankedArea[]) => ({
      date: d.date,
      total_tracked_mins: d.total_tracked_mins,
      active_mins: d.active_mins,
      idle_mins: d.idle_mins,
      first_activity: d.first_activity,
      last_activity: d.last_activity,
      punches: (d.tracking_sessions || []).length,
      // Today's figures are a day in progress, not a day's total. Without
      // this the model reads twenty minutes at 11am as a barely-worked day
      // and says so.
      in_progress: d.date === format(new Date(), "yyyy-MM-dd"),
      // The SAME label the tables render. Sent raw, the model wrote
      // "localhost:3000/activity-comparison" into a sentence sitting above a
      // row that said "Activity Comparison (local)", and a reader cannot tell
      // those are one thing.
      work_areas: list.slice(0, 12).map((a) => ({
        area: prettyArea(a.area), mins: a.mins, details: a.details,
      })),
      top_apps: (d.app_usage || []).slice(0, 8).map((a) => ({
        name: a.app_name, mins: a.duration_mins,
      })),
    });
    try {
      const { data } = await api.post("/api/system-level-tracking/ai-comparison", {
        employee_id: employeeId,
        employee_name: employeeName,
        before: asDay(before, areas.before),
        after: asDay(after, areas.after),
      });
      if (data?.ok) setAi(data as AiComparison);
      else setAiError(data?.message || "Couldn't generate a comparison.");
    } catch (err: any) {
      setAiError(err?.response?.data?.message || "AI is unavailable right now.");
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
          <CalendarRange className="h-3.5 w-3.5" />
          Day vs Day — {fmtDate(before.date)} → {fmtDate(after.date)}
        </p>
        <Button
          onClick={askAi}
          disabled={aiLoading}
          size="sm"
          variant="outline"
          className="h-7 px-3 text-[11px]"
        >
          {aiLoading ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Sparkles className="h-3 w-3 mr-1" />
          )}
          {ai ? "Regenerate" : "AI comparison"}
        </Button>
      </div>

      {aiError && (
        <div className="p-2.5 bg-red-50 border border-red-200 rounded-lg text-[11px] text-red-700">
          {aiError}
        </div>
      )}

      {/* The paragraph and the table it was written from, in ONE card.

          They used to be two, with a metrics table in between: the AI sentence
          up here, the figures it describes further down the page. A claim is
          only worth something checked, and nobody carries a sentence past two
          section headings to check it. Now the table is the next thing under
          the sentence, and the card holds the whole answer.

          The card renders with or without the AI text — the counted table is
          the part that is always true, and the paragraph is the part that is
          optional. */}
      <Card>
        <CardContent className="p-0">
          <div className="p-3 space-y-3">
            {/* How much there was to compare, before anything is concluded
                from it. A long "stopped" column beside six tracked minutes is
                a gap in the tracking, not a change in the work. */}
            <div className="flex items-center gap-2 flex-wrap text-[10px] text-slate-500">
              <span>
                <span className="font-semibold text-slate-700">{fmtDate(before.date)}</span>
                {" — "}{fmtMins(before.total_tracked_mins)} tracked
              </span>
              <span className="text-slate-300">·</span>
              <span>
                <span className="font-semibold text-slate-700">{fmtDate(after.date)}</span>
                {" — "}{fmtMins(after.total_tracked_mins)} tracked
              </span>
            </div>

            {ai ? (
              <>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <p className="text-[12px] text-slate-800 leading-snug flex-1 min-w-[240px]">
                    {ai.verdict}
                  </p>
                  {/* Said out loud, not buried. A confident-sounding paragraph
                      written over twenty minutes of tracking is the thing this
                      whole panel has to protect a reader from. */}
                  <span
                    className={`text-[9px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full whitespace-nowrap ${
                      ai.confidence === "high"
                        ? "bg-emerald-50 text-emerald-700"
                        : "bg-amber-50 text-amber-700"
                    }`}
                  >
                    {ai.confidence === "high" ? "confident" : "thin data"}
                  </span>
                </div>

                {/* A caveat in grey 10px under a fluent paragraph gets read
                    last, if at all — and it is the one line that decides
                    whether any of the rest means anything. */}
                {ai.confidence !== "high" ? (
                  <div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 p-2">
                    <AlertCircle className="h-3 w-3 text-amber-600 mt-[1px] shrink-0" />
                    <p className="text-[10.5px] text-amber-800 leading-snug">
                      {afterIsToday
                        ? `Today is still going — ${fmtMins(after.total_tracked_mins)} tracked so far, `
                          + `against ${fmtMins(before.total_tracked_mins)} for the whole of `
                          + `${fmtDate(before.date)}. Worth re-reading later in the day.`
                        : ai.confidence_note
                          || `Only ${fmtMins(after.total_tracked_mins)} was tracked on `
                             + `${fmtDate(after.date)}, so the two days cannot really be compared.`}
                    </p>
                  </div>
                ) : (
                  ai.confidence_note && (
                    <p className="text-[10.5px] text-slate-500 leading-snug">
                      {ai.confidence_note}
                    </p>
                  )
                )}
              </>
            ) : (
              <p className="text-[11px] text-slate-500 leading-snug">
                Below is what each day went on, counted from the tracked minutes.
                Press{" "}
                <span className="font-medium text-slate-700">AI comparison</span> for a
                sentence reading one day against the other.
              </p>
            )}

            {/* What they were working ON, above which app they had open. An
                app name does not answer "did yesterday's task get any time
                today"; a repo, a client folder or a portal section does.

                One table with a column per day, rather than two lists side by
                side with three summary lists above them. The two days still
                sit next to each other — that is what the two date columns are
                — but an area appears exactly once, with each day's files in
                that day's column. */}
            <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 pt-1">
              <Briefcase className="h-3.5 w-3.5" />
              What each day went on — {fmtDate(before.date)} vs {fmtDate(after.date)}
            </p>
          </div>

          <AreaTable
            rows={rows}
            beforeDate={before.date}
            afterDate={after.date}
            // The Change column is the only part that reads one day AGAINST
            // the other, so it is the only part a thin day invalidates. 9
            // minutes against 4h29m marked 23 areas "stopped", every one of
            // them an artefact of the day not being recorded. The minutes
            // themselves are still true, so the table stays and only the
            // verdict column goes.
            showChange={comparable}
            afterInProgress={afterIsToday}
          />

          <div className="p-3 pt-2 space-y-1.5">
            {!comparable && (
              <p className="text-[10.5px] text-slate-500 leading-snug">
                {afterIsToday ? (
                  <>
                    The change column appears once {fmtDate(after.date)} has about half
                    an hour on it — right now most of {fmtDate(before.date)} would read
                    as “stopped” simply because today has not reached it yet.
                  </>
                ) : (
                  <>
                    The change column is hidden: with only
                    {" "}{fmtMins(Math.min(before.total_tracked_mins, after.total_tracked_mins))}
                    {" "}tracked on one of these days, every line of it would be about
                    the tracking, not about the work.
                  </>
                )}
              </p>
            )}
            <p className="text-[9.5px] text-slate-400 leading-snug">
              Area names come from window titles and URLs — they say which screen was
              open, not which task it was for.
              {ai ? " The sentence above is written by AI; check it against the figures." : ""}
            </p>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50">
                <th className="py-2 pl-3 pr-2 text-left text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  Metric
                </th>
                <th className="py-2 px-2 text-right text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  {fmtDate(before.date)}
                </th>
                <th className="py-2 px-2 text-right text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  {fmtDate(after.date)}
                </th>
                <th className="py-2 pl-2 pr-3 text-right text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                  Change
                </th>
              </tr>
            </thead>
            <tbody>
              {metrics.map((m) => (
                <DeltaRow key={m.key} m={m} />
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* The label sits outside the card so the dated header row is the only
          grey band on it — stacked, the two read as one confused header. */}
      <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5 pt-1">
        <Monitor className="h-3.5 w-3.5" />
        What changed by app — {fmtDate(before.date)} vs {fmtDate(after.date)}
      </p>

      <Card>
        <CardContent className="p-0">
          {!moved.length ? (
            <p className="px-3 py-4 text-[11px] text-slate-500 text-center">
              {apps.length
                ? "Same apps, same time on both days."
                : "No app usage recorded on either day."}
            </p>
          ) : (
            <table className="w-full">
              {/* Dated, like the metric table above. Without these the two
                  middle columns are just two numbers and you have to remember
                  which way round the comparison runs. */}
              <thead>
                <tr className="bg-slate-50">
                  <th className="py-2 pl-3 pr-2 text-left text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                    App
                  </th>
                  <th className="py-2 px-2 text-right text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                    {fmtDate(before.date)}
                  </th>
                  <th className="py-2 px-2 text-right text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                    {fmtDate(after.date)}
                  </th>
                  <th className="py-2 pl-2 pr-3 text-right text-[9px] uppercase tracking-wider text-slate-500 font-semibold">
                    Change
                  </th>
                </tr>
              </thead>
              <tbody>
                {moved.map((a) => (
                  <tr key={a.app} className="border-t border-slate-100">
                    <td className="py-2 pl-3 pr-2 text-[11px] text-slate-700 truncate max-w-[180px]">
                      {a.app}
                    </td>
                    <td className="py-2 px-2 text-[11px] text-right tabular-nums text-slate-500">
                      {a.before ? fmtHours(a.before / 60) : "—"}
                    </td>
                    <td className="py-2 px-2 text-[11px] text-right tabular-nums text-slate-700">
                      {a.after ? fmtHours(a.after / 60) : "—"}
                    </td>
                    <td
                      className={`py-2 pl-2 pr-3 text-[11px] text-right tabular-nums font-semibold ${
                        a.delta > 0 ? "text-emerald-600" : "text-rose-600"
                      }`}
                    >
                      {fmtDeltaMins(a.delta)}
                      {/* Appearing and vanishing are the two most readable
                          findings here, so they get said rather than inferred
                          from a dash in one column. */}
                      {a.before === 0 && (
                        <span className="ml-1 font-normal opacity-70">(new)</span>
                      )}
                      {a.after === 0 && (
                        <span className="ml-1 font-normal opacity-70">(gone)</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ─── Main Dashboard ─────────────────────────────────────────────────────────────

export const AWADashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<"daily" | "individual">("daily");

  // ── Daily state ──
  // allDailyRecords = full unfiltered source of truth from API
  const [allDailyRecords, setAllDailyRecords] = useState<EmployeeRecord[]>([]);
  const [dailyMeta, setDailyMeta] = useState<{
    work_date: string;
    total_employees: number;
  } | null>(null);
  const [selectedDate, setSelectedDate] = useState(
    format(new Date(), "yyyy-MM-dd"),
  );

  // ── Individual state ──
  const [individualRecords, setIndividualRecords] = useState<EmployeeRecord[]>(
    [],
  );
  const [individualMeta, setIndividualMeta] = useState<{
    employeeID: string;
    employeeName: string;
    start_date: string;
    end_date: string;
  } | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(
    null,
  );
  // Two DAYS to put side by side, not a range. "What did he do on the 12th,
  // and what is he doing today" is the question — a span from the 1st to the
  // 13th answers a different one, and made the two middle columns of every
  // comparison ambiguous. Defaults to yesterday against today.
  const [dayOne, setDayOne] = useState(
    format(new Date(Date.now() - 86400000), "yyyy-MM-dd"),
  );
  const [dayTwo, setDayTwo] = useState(format(new Date(), "yyyy-MM-dd"));
  // What WorkMonitor recorded for each day of the chosen range. Separate from
  // individualRecords: that is the AWA report (attendance vs work monitor vs
  // work activity for a day), this is the tracking row the day-vs-day
  // comparison is built from.
  const [trackingDays, setTrackingDays] = useState<TrackingDay[]>([]);

  // ── Common state ──
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [openEmpDropdown, setOpenEmpDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilterType>("All");
  const [loading, setLoading] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  // The two ends of whatever the range returned. null when the range holds
  // fewer than two tracked days — a day compared with itself is a wall of
  // zeroes that reads as a broken report.
  const dayComparison = useMemo(() => {
    const pair = pickComparisonDays(trackingDays);
    return pair ? compareDays(pair.before, pair.after) : null;
  }, [trackingDays]);

  // Fetch employees on mount
  useEffect(() => {
    (async () => {
      setLoadingEmployees(true);
      try {
        const res = await getEmployees();
        setEmployees(res?.employees || []);
      } catch {
        toast({
          title: "Warning",
          description: "Could not fetch employees.",
          variant: "destructive",
        });
      } finally {
        setLoadingEmployees(false);
      }
    })();
  }, []);

  // ── Fetch daily report ─────────────────────────────────────────────────────
  const fetchDailyReport = async () => {
    if (!selectedDate) {
      setError("Please select a date");
      return;
    }
    setLoading(true);
    setError(null);
    // Reset search/filter whenever new data is loaded
    setSearchQuery("");
    setStatusFilter("All");

    try {
      const res = await api.get("/awa-report", {
        params: { work_date: selectedDate },
      });
      const records = normalizeReport(res.data);

      if (records.length > 0) {
        setAllDailyRecords(records);

        // Build meta
        if (!Array.isArray(res.data) && res.data?.work_date) {
          setDailyMeta({
            work_date: res.data.work_date,
            total_employees: res.data.total_employees ?? records.length,
          });
        } else {
          setDailyMeta({
            work_date: records[0]?.date || selectedDate,
            total_employees: records.length,
          });
        }

        toast({
          title: "Success",
          description: `Report loaded for ${fmtDate(selectedDate)}`,
        });
      } else {
        setAllDailyRecords([]);
        setDailyMeta(null);
        setError(`No data found for ${fmtDate(selectedDate)}`);
        toast({
          title: "No Data",
          description: "No records for selected date.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("fetchDailyReport:", err);
      setError("Failed to fetch report. Please check your connection.");
      toast({
        title: "Error",
        description: "Failed to fetch report.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // ── Fetch individual report ────────────────────────────────────────────────
  const fetchIndividualReport = async () => {
    if (!selectedEmployee) {
      toast({
        title: "Error",
        description: "Select an employee.",
        variant: "destructive",
      });
      return;
    }
    // Order does not matter any more — these are two days, not the ends of a
    // range, so picking them the "wrong way round" is not a mistake to refuse.
    // Sorted here so the comparison always reads earlier → later.
    const [earlier, later] = [dayOne, dayTwo].sort();

    setLoading(true);
    setError(null);
    setTrackingDays([]);

    // What WorkMonitor itself recorded, fetched beside the AWA report. The AWA
    // report carries in/out/idle per source; the day-vs-day comparison needs
    // the tracking row's own numbers — apps, punches, the active/idle split.
    // Its own try/catch: a missing comparison must not fail the report that
    // this tab was already showing before the comparison existed.
    // One call per day, not one call spanning them. Asking for 1 Oct → 13 Oct
    // returns thirteen days of which the report uses two, and the extra eleven
    // land in the Daily Breakdown where nobody asked for them.
    try {
      const days = earlier === later ? [earlier] : [earlier, later];
      const fetched = await Promise.all(
        days.map((d) =>
          api.get("/api/system-level-tracking/employees", {
            params: {
              employee_id: selectedEmployee.employeeID,
              start_date: d,
              end_date: d,
              limit: 100,
              offset: 0,
            },
          }),
        ),
      );
      setTrackingDays(
        fetched.flatMap((t) => (Array.isArray(t.data?.data) ? t.data.data : [])),
      );
    } catch (err) {
      console.error("fetchIndividualReport (tracking):", err);
    }

    try {
      // Same again: one request per day, so the breakdown below holds exactly
      // the days that were picked.
      const days = earlier === later ? [earlier] : [earlier, later];
      const responses = await Promise.all(
        days.map((d) =>
          api.get("/awa-report/employee", {
            params: {
              employee_id: selectedEmployee.id || selectedEmployee.employeeID,
              start_date: d,
              end_date: d,
            },
          }),
        ),
      );
      const res = responses[responses.length - 1];
      const records = responses.flatMap((r) => normalizeReport(r.data));

      if (records.length > 0) {
        setIndividualRecords(records);

        let empName =
          `${selectedEmployee.first_name || ""} ${selectedEmployee.last_name || ""}`.trim();
        let empID = selectedEmployee.employeeID;

        if (!Array.isArray(res.data) && res.data?.employeeName) {
          empName = res.data.employeeName;
          empID = res.data.employeeID || empID;
        } else if (records[0]?.employeeName) {
          empName = records[0].employeeName;
          empID = records[0].employeeID || empID;
        }

        setIndividualMeta({
          employeeID: empID,
          employeeName: empName,
          start_date: earlier,
          end_date: later,
        });
        toast({
          title: "Success",
          description: `Report loaded for ${empName}`,
        });
      } else {
        setIndividualRecords([]);
        setIndividualMeta(null);
        setError("No data found for that employee on the days picked.");
        toast({
          title: "No Data",
          description: "No records found.",
          variant: "destructive",
        });
      }
    } catch (err) {
      console.error("fetchIndividualReport:", err);
      setError("Failed to fetch employee report.");
      toast({
        title: "Error",
        description: "Failed to fetch employee report.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-load daily report on date / tab change
  useEffect(() => {
    if (activeTab === "daily") fetchDailyReport();
  }, [selectedDate, activeTab]);

  // ── filteredDaily: computed from allDailyRecords + search + statusFilter ──
  const filteredDaily = useMemo(() => {
    let list = allDailyRecords;

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase().trim();
      list = list.filter(
        (e) =>
          e.employeeName.toLowerCase().includes(q) ||
          e.employeeID.toLowerCase().includes(q),
      );
    }

    if (statusFilter !== "All") {
      list = list.filter((e) => getOverallStatus(e.status) === statusFilter);
    }

    return list;
  }, [allDailyRecords, searchQuery, statusFilter]);

  // ── Summary stats — always from full unfiltered list ──
  const summaryStats = useMemo(
    () => ({
      present: allDailyRecords.filter(
        (e) => e.status.present && !e.status.work_from_home,
      ).length,
      absent: allDailyRecords.filter((e) => e.status.absent).length,
      leave: allDailyRecords.filter((e) => e.status.leave).length,
      wfh: allDailyRecords.filter((e) => e.status.work_from_home).length,
    }),
    [allDailyRecords],
  );

  // ── Employee dropdown search ──
  const filteredEmployees = useMemo(() => {
    if (!employeeSearch) return employees;
    const q = employeeSearch.toLowerCase();
    return employees.filter(
      (e) =>
        e.first_name?.toLowerCase().includes(q) ||
        e.last_name?.toLowerCase().includes(q) ||
        e.employeeID.toLowerCase().includes(q),
    );
  }, [employees, employeeSearch]);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div
      className="min-h-screen"
      style={{ background: "var(--gt-surface-1)" }}
    >
      {/* Header */}
      <div
        className="sticky top-0 z-20"
        style={{
          background: "var(--gt-surface-0)",
          borderBottom: "1px solid var(--gt-border)",
          boxShadow: "var(--gt-elev-1)",
        }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center"
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: "var(--gt-primary)",
              }}
            >
              <Activity className="h-4 w-4" style={{ color: "var(--gt-on-primary)" }} />
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontFamily: "var(--gt-font-display)",
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: "-0.02em",
                  color: "var(--gt-text-primary)",
                  lineHeight: 1.1,
                }}
              >
                AWA Report
              </h1>
              <p
                style={{
                  fontSize: 11,
                  color: "var(--gt-text-tertiary)",
                  marginTop: 2,
                  fontWeight: 500,
                }}
              >
                Attendance &amp; Work Activity
              </p>
            </div>
          </div>
          <Tabs
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as "daily" | "individual")}
          >
            <TabsList className="h-7 p-0.5">
              <TabsTrigger value="daily" className="text-[10px] px-3 h-6">
                Daily Report
              </TabsTrigger>
              <TabsTrigger value="individual" className="text-[10px] px-3 h-6">
                Employee Report
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-4 space-y-4">
        {/* App Updates used to sit here — the installer download cards. They
            belong on the Downloads page, which is where people look for them;
            on a report they pushed the thing you came for below the fold. */}

        {/* Error */}
        {error && (
          <div className="flex items-center gap-2 p-2.5 bg-red-50 border border-red-200 rounded-lg text-red-700 text-xs">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" /> {error}
          </div>
        )}

        {/* ══ DAILY TAB ══ */}
        {activeTab === "daily" && (
          <>
            {/* Date picker */}
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className={DATE_INPUT_CLASS}
              />
              <Button
                onClick={fetchDailyReport}
                disabled={loading}
                size="sm"
                className="h-8 px-4 text-xs bg-slate-800 hover:bg-slate-700"
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Search className="h-3 w-3 mr-1" />
                )}
                Load
              </Button>
            </div>

            {allDailyRecords.length > 0 && dailyMeta && (
              <>
                {/* Info bar */}
                <div className="flex items-center justify-between p-2 bg-white border border-slate-200 rounded-lg">
                  <div className="flex items-center gap-1.5 text-[10px] text-slate-600">
                    <CalendarRange className="h-3 w-3" />
                    <span className="font-medium">
                      {fmtDate(dailyMeta.work_date)}
                    </span>
                  </div>
                  <span className="text-[10px] text-slate-500">
                    Total: {dailyMeta.total_employees} employees
                  </span>
                </div>

                {/* Summary stats (always from full unfiltered data) */}
                <div className="grid grid-cols-4 gap-2">
                  {[
                    {
                      label: "Present",
                      value: summaryStats.present,
                      color: "bg-emerald-50 text-emerald-700",
                    },
                    {
                      label: "Absent",
                      value: summaryStats.absent,
                      color: "bg-red-50 text-red-700",
                    },
                    {
                      label: "Leave",
                      value: summaryStats.leave,
                      color: "bg-blue-50 text-blue-700",
                    },
                    {
                      label: "WFH",
                      value: summaryStats.wfh,
                      color: "bg-purple-50 text-purple-700",
                    },
                  ].map((s) => (
                    <div
                      key={s.label}
                      className={`rounded-lg p-2 text-center ${s.color}`}
                    >
                      <p className="text-[9px] font-medium uppercase tracking-wide">
                        {s.label}
                      </p>
                      <p className="text-base font-bold">{s.value}</p>
                    </div>
                  ))}
                </div>

                {/* Search + Filter */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                    <Input
                      placeholder="Search by name or employee ID..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-7 h-8 text-xs bg-white"
                    />
                  </div>
                  <div className="flex gap-1.5 flex-wrap">
                    {(
                      [
                        "All",
                        "Present",
                        "Absent",
                        "Leave",
                        "WFH",
                      ] as StatusFilterType[]
                    ).map((s) => (
                      <Button
                        key={s}
                        size="sm"
                        variant={statusFilter === s ? "default" : "outline"}
                        onClick={() => setStatusFilter(s)}
                        className={`h-7 px-2.5 text-[10px] ${statusFilter === s ? "bg-slate-800 text-white" : ""}`}
                      >
                        {s}
                        {s !== "All" && (
                          <span className="ml-1 opacity-60">
                            (
                            {
                              allDailyRecords.filter(
                                (e) => getOverallStatus(e.status) === s,
                              ).length
                            }
                            )
                          </span>
                        )}
                      </Button>
                    ))}
                  </div>
                </div>

                {/* Showing count */}
                {(searchQuery.trim() || statusFilter !== "All") && (
                  <p className="text-[10px] text-slate-500">
                    Showing {filteredDaily.length} of {allDailyRecords.length}{" "}
                    employees
                  </p>
                )}

                {/* Cards */}
                {filteredDaily.length === 0 ? (
                  <div className="text-center py-10 text-slate-400 text-sm">
                    No employees match your search or filter.
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredDaily.map((emp) => (
                      <EmployeeTableCard key={emp.employeeID} emp={emp} />
                    ))}
                  </div>
                )}
              </>
            )}
          </>
        )}

        {/* ══ INDIVIDUAL TAB ══ */}
        {activeTab === "individual" && (
          <>
            <div className="flex flex-col sm:flex-row gap-2 flex-wrap">
              {/* Employee picker */}
              <Popover open={openEmpDropdown} onOpenChange={setOpenEmpDropdown}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="justify-between h-8 text-xs bg-white flex-1 max-w-xs"
                  >
                    {selectedEmployee ? (
                      <span className="truncate">
                        {selectedEmployee.first_name}{" "}
                        {selectedEmployee.last_name}
                      </span>
                    ) : (
                      <span className="text-slate-400">Select employee...</span>
                    )}
                    <ChevronDown className="ml-1 h-3 w-3 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search employee..."
                      value={employeeSearch}
                      onValueChange={setEmployeeSearch}
                      className="h-8 text-xs"
                    />
                    <CommandList>
                      <CommandEmpty className="text-xs py-2">
                        {loadingEmployees ? "Loading..." : "No employee found."}
                      </CommandEmpty>
                      <CommandGroup>
                        {filteredEmployees.map((emp) => (
                          <CommandItem
                            key={emp.employeeID}
                            value={`${emp.first_name} ${emp.last_name} ${emp.employeeID}`}
                            onSelect={() => {
                              setSelectedEmployee(emp);
                              setEmployeeSearch("");
                              setOpenEmpDropdown(false);
                            }}
                            className="cursor-pointer text-xs"
                          >
                            <Check
                              className={`mr-2 h-3 w-3 ${
                                selectedEmployee?.employeeID === emp.employeeID
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                            />
                            <div>
                              <p className="text-xs font-medium">
                                {emp.first_name} {emp.last_name}
                              </p>
                              <p className="text-[9px] text-slate-500">
                                {emp.employeeID}
                              </p>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Input
                type="date"
                value={dayOne}
                onChange={(e) => setDayOne(e.target.value)}
                className={DATE_INPUT_CLASS}
              />
              {/* "vs", not "to": these are two days being compared, and "to"
                  read as a span from one to the other. */}
              <span className="self-center text-xs font-medium text-slate-500">
                vs
              </span>
              <Input
                type="date"
                value={dayTwo}
                onChange={(e) => setDayTwo(e.target.value)}
                className={DATE_INPUT_CLASS}
              />
              <Button
                onClick={fetchIndividualReport}
                disabled={loading || !selectedEmployee}
                size="sm"
                className="h-8 px-4 text-xs bg-slate-800 hover:bg-slate-700"
              >
                {loading ? (
                  <Loader2 className="h-3 w-3 animate-spin mr-1" />
                ) : (
                  <Search className="h-3 w-3 mr-1" />
                )}
                Load
              </Button>
            </div>

            {!selectedEmployee && !loading && (
              <div className="text-center py-12">
                <Users className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-sm text-slate-500">
                  Select an employee to view their report
                </p>
              </div>
            )}

            {individualMeta && individualRecords.length > 0 && (
              <>
                {/* A light card, like everything else on the page. This was a
                    dark slate bar with opacity-70 text on it, which came out
                    as grey-on-dark and could not be read at all — and it is
                    the line that says WHICH employee and WHICH dates you are
                    looking at, so it is the last thing that should be faint. */}
                <div className="p-3 bg-white border border-slate-200 rounded-lg flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <div className="h-8 w-8 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <User className="h-4 w-4 text-slate-500" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-slate-900 truncate">
                        {individualMeta.employeeName}
                      </p>
                      <p className="text-[10px] text-slate-500">
                        {individualMeta.employeeID}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    {/* Two days named, or one when both pickers are the same
                        day — "1 day(s)" said nothing about WHICH. */}
                    <p className="text-xs font-medium text-slate-700">
                      {individualMeta.start_date === individualMeta.end_date
                        ? fmtDate(individualMeta.start_date)
                        : `${fmtDate(individualMeta.start_date)} vs ${fmtDate(individualMeta.end_date)}`}
                    </p>
                    <p className="text-[10px] text-slate-500">
                      {individualMeta.start_date === individualMeta.end_date
                        ? "single day"
                        : "two days compared"}
                    </p>
                  </div>
                </div>

                {/* Day vs day, above the per-day cards: "what changed" is the
                    question somebody picks two days to ask, and the breakdown
                    below answers "what happened" on each of them. */}
                {dayComparison ? (
                  <DayComparisonPanel
                    comparison={dayComparison}
                    employeeId={individualMeta.employeeID}
                    employeeName={individualMeta.employeeName}
                  />
                ) : (
                  dayOne !== dayTwo && (
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-[11px] text-amber-800">
                        No comparison: WorkMonitor recorded nothing on
                        {" "}{trackingDays.length === 1 ? "one of these days" : "either of these days"}.
                        Both need a tracked summary before they can be compared.
                      </p>
                    </div>
                  )
                )}

                <div className="space-y-3">
                  <p className="text-xs font-semibold text-slate-700 flex items-center gap-1.5">
                    <FileText className="h-3.5 w-3.5" />
                    Each day in detail ({individualRecords.length} day{individualRecords.length === 1 ? "" : "s"})
                  </p>
                  {individualRecords.map((rec, i) => (
                    <EmployeeTableCard
                      key={`${rec.employeeID}-${i}`}
                      emp={rec}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {/* Loader */}
        {loading && (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-slate-500" />
          </div>
        )}
      </div>
    </div>
  );
};

export default AWADashboard;
